import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlarmClock,
  AlertTriangle,
  ArrowRight,
  Bell,
  CalendarClock,
  CheckCheck,
  UserPlus,
  UsersRound,
} from "lucide-react";
import { format } from "date-fns";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import UserAvatar from "@/components/UserAvatar";
import AnimatedHeight from "@/components/shared/AnimatedHeight";
import SegmentedTabs from "@/components/shared/SegmentedTabs";
import { useMentions, useMarkMentionAsRead, useMarkAllMentionsAsRead, type Mention } from "@/hooks/useMentions";
import {
  useAssignmentNotifications,
  useMarkAssignmentAsRead,
  useMarkAllNotificationsAsRead,
  type AssignmentNotification,
} from "@/hooks/useNotifications";
import {
  useMarkTicketNotificationsRead,
  useTicketNotifications,
  type TicketNotification,
} from "@/hooks/useTicketNotifications";
import { getDisplayName } from "@/lib/userUtils";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { ageLabel } from "./WorkRow";

type Entry =
  | { type: "mention"; id: string; data: Mention; created_at: string; is_read: boolean }
  | { type: "assignment"; id: string; data: AssignmentNotification; created_at: string; is_read: boolean }
  | { type: "ticket"; id: string; data: TicketNotification; created_at: string; is_read: boolean };

/** A target date (YYYY-MM-DD) as "Jun 12", read as the calendar day it is. */
const dueDay = (isoDate: string | null) => (isoDate ? format(new Date(`${isoDate}T00:00:00`), "MMM d") : "");

type Tab = "all" | "mentions" | "assignments";

const TABS: Array<{ key: Tab; label: string }> = [
  { key: "all", label: "All" },
  { key: "mentions", label: "Mentions" },
  // "Assigned to you", not "Assignments". The noun named the RECORD — a row in
  // assignment_notifications — where the other two tabs name what the reader
  // will find. This tab is the half of Catch Up that says somebody put work on
  // you, which is the same sentence Home's My work panel is built around now
  // that its buckets are assigned-to-me only.
  { key: "assignments", label: "Assigned to you" },
];

/** Entries shown before "Full history" takes over — a panel, not an archive. */
const PANEL_CAP = 8;

/**
 * Catch Up as a Home panel — mentions and assignments in view on the
 * dashboard, instead of tucked behind the avatar menu. The /mentions page
 * stays as the full history; this shows what's current.
 */
const CatchUpPanel = () => {
  const navigate = useNavigate();
  const { data: mentions = [] } = useMentions();
  const { data: assignments = [] } = useAssignmentNotifications();
  // Due soon, overdue and reminders (#9112). Shown under All only: the panel
  // is too narrow for a fourth tab, and /mentions has one.
  const { data: ticketNotifications = [] } = useTicketNotifications();
  const markTicketNotificationsRead = useMarkTicketNotificationsRead();
  const markMentionAsRead = useMarkMentionAsRead();
  const markAllMentionsAsRead = useMarkAllMentionsAsRead();
  const markAssignmentAsRead = useMarkAssignmentAsRead();
  const markAllAssignmentsAsRead = useMarkAllNotificationsAsRead();

  const [tab, setTab] = useState<Tab>("all");

  const entries = useMemo((): Entry[] => {
    const all: Entry[] = [];
    for (const m of mentions) {
      if (!m.comment?.backlog_item || m.comment.backlog_item.deleted_at) continue;
      all.push({ type: "mention", id: `m-${m.id}`, data: m, created_at: m.created_at, is_read: m.is_read });
    }
    for (const a of assignments) {
      all.push({ type: "assignment", id: `a-${a.id}`, data: a, created_at: a.created_at, is_read: a.is_read });
    }
    for (const t of ticketNotifications) {
      all.push({ type: "ticket", id: `t-${t.id}`, data: t, created_at: t.created_at, is_read: t.is_read });
    }
    all.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    return all;
  }, [mentions, assignments, ticketNotifications]);

  const unreadCount = entries.filter((e) => !e.is_read).length;

  const visible = useMemo(() => {
    const filtered =
      tab === "mentions"
        ? entries.filter((e) => e.type === "mention")
        : tab === "assignments"
          ? entries.filter((e) => e.type === "assignment")
          : entries;
    return { list: filtered.slice(0, PANEL_CAP), total: filtered.length };
  }, [entries, tab]);

  const openEntry = (entry: Entry) => {
    if (entry.type === "mention") {
      if (!entry.data.is_read) markMentionAsRead.mutate(entry.data.id);
      navigate(`/item/${entry.data.comment!.backlog_item_id}?comment=${entry.data.comment!.id}`);
    } else if (entry.type === "ticket") {
      if (!entry.data.is_read) markTicketNotificationsRead.mutate(entry.data.id);
      navigate(`/item/${entry.data.backlog_item_id}`);
    } else {
      if (!entry.data.is_read) markAssignmentAsRead.mutate(entry.data.id);
      navigate(`/item/${entry.data.backlog_item_id}`);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await Promise.all([
        markAllMentionsAsRead.mutateAsync(),
        markAllAssignmentsAsRead.mutateAsync(),
        ...(ticketNotifications.some((t) => !t.is_read) ? [markTicketNotificationsRead.mutateAsync("all")] : []),
      ]);
      toast.success("All caught up");
    } catch {
      toast.error("Failed to mark all as read");
    }
  };

  const entryLine = (entry: Entry) => {
    if (entry.type === "ticket") {
      const n = entry.data;
      if (n.kind === "due_soon") return <b>Due {dueDay(n.due_on)}</b>;
      if (n.kind === "overdue") {
        return (
          <>
            <b>Overdue</b> since {dueDay(n.due_on)}
          </>
        );
      }
      return (
        <>
          <b>Reminder</b>
          {n.note ? `: ${n.note}` : ""}
        </>
      );
    }
    if (entry.type === "mention") {
      const who = entry.data.comment?.user ? getDisplayName(entry.data.comment.user) : "Someone";
      return (
        <>
          <b>{who}</b> mentioned you
        </>
      );
    }
    const who = entry.data.assigned_by ? getDisplayName(entry.data.assigned_by) : "Someone";
    if (entry.data.team) {
      return (
        <>
          <b>{who}</b> put this on <b>{entry.data.team.name}</b>
        </>
      );
    }
    return (
      <>
        <b>{who}</b> assigned you
      </>
    );
  };

  return (
    /* `.catch-up` scopes the bell's hover performance to the whole panel: the
       bell is a 16px icon nobody would aim at, but the panel it labels is the
       thing the cursor actually arrives on. See `ux-bell-ring` in index.css. */
    /* `h-full` so the panel fills the grid row My work sets, and the footer
       lands on the same baseline as the card beside it instead of floating
       halfway up a column of empty page. */
    /* `insight-card` alongside `catch-up`: the panel sits in the same grid as
       the three insight cards below it, and it was the one card on Home that
       did not answer the cursor — so it read as inert next to neighbours that
       lift. It borrows their exact hover recipe (lift, ring border, shadow —
       see .insight-card in index.css) rather than growing a near-copy. Its own
       `.catch-up` hover (the bell ring) still runs; the two share nothing. */
    <Card className="catch-up insight-card flex h-full min-w-0 flex-col border-border">
      {/* 51px, and the two extra pixels are the whole point: this header's
          rule has to land on the same y as the My work panel's beside it (see
          the note there), and that card carries a 3px top RAIL where this one
          carries a 1px border. Its content therefore starts 2px lower, so
          this header has to be 2px taller for the two rules to line up. */}
      <div className="flex min-h-[51px] items-center gap-2 border-b border-border/70 px-4 py-3">
        {/* dark: --primary is navy-on-near-black there (the .th-active
            problem, index.css), so the panel's navy accents point at the ring
            cyan in dark. Applies to the bell, the "@"/assignment discs and
            the unread dot below. */}
        <Bell className="catch-up__bell h-4 w-4 text-primary dark:text-[hsl(var(--ring))]" />
        <span className="text-[13.5px] font-extrabold">Catch Up</span>
        {unreadCount > 0 && (
          <span className="rounded-full bg-primary px-2 py-0.5 text-[11px] font-bold text-primary-foreground">
            {unreadCount}
          </span>
        )}
        {unreadCount > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleMarkAllAsRead}
            title="Mark everything as read"
            className="ml-auto h-7 gap-1.5 px-2 text-xs text-muted-foreground"
          >
            <CheckCheck className="h-3.5 w-3.5" />
            Mark all read
          </Button>
        )}
      </div>

      {/* segmented tabs — the same three views the full page offers, and the
          same control as the My work card so Home has one tab style. Labels
          only, and that matters more since the third one became the sentence
          "Assigned to you": this panel is ~340px wide, so the three segments
          have about 108px each, and a count chip beside that label would wrap
          the segment. The counts are in the rows themselves. */}
      <div className="px-3 pt-3">
        <SegmentedTabs ariaLabel="Catch Up filter" tabs={TABS} value={tab} onChange={setTab} />
      </div>

      {/* Height-animated, same reason as the My work card: the three filters
          rarely hold the same number of entries. `flex-1` stays on a plain
          wrapper with the animated box inside it — a `flex-basis: 0` item takes
          its size from flex distribution, so a pixel height set on the item
          itself is ignored and nothing animates. */}
      {/* `min-h-0` + `overflow-y-auto`: the entries take whatever height is
          left between the tabs and the footer, and a full inbox scrolls inside
          the panel rather than pushing the card past its neighbour. */}
      <div className="min-h-0 flex-1 overflow-y-auto">
        <AnimatedHeight innerClassName="p-1.5">
          {visible.list.length === 0 ? (
            <p className="px-3 py-4 text-sm text-muted-foreground">
              {tab === "all" ? "Nothing to catch up on — nice." : `No ${tab} yet.`}
            </p>
          ) : (
            visible.list.map((entry) => (
              <div
                key={entry.id}
                onClick={() => openEntry(entry)}
                className={cn(
                  "flex cursor-pointer items-start gap-2.5 rounded-lg px-2.5 py-2 transition-colors hover:bg-[hsl(var(--table-row-hover))]",
                  !entry.is_read && "bg-secondary/50",
                )}
              >
                {entry.type === "mention" ? (
                  entry.data.comment?.user ? (
                    <UserAvatar
                      avatarUrl={entry.data.comment.user.avatar_url}
                      firstName={entry.data.comment.user.first_name}
                      lastName={entry.data.comment.user.last_name}
                      email={entry.data.comment.user.email}
                      className="mt-0.5 h-6 w-6"
                      fallbackClassName="text-[10px]"
                    />
                  ) : (
                    <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-secondary text-[11px] font-extrabold text-primary dark:text-[hsl(var(--ring))]">
                      @
                    </span>
                  )
                ) : entry.type === "ticket" ? (
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-secondary text-primary dark:text-[hsl(var(--ring))]">
                    {entry.data.kind === "due_soon" ? (
                      <CalendarClock className="h-3.5 w-3.5" />
                    ) : entry.data.kind === "overdue" ? (
                      <AlertTriangle className="h-3.5 w-3.5" />
                    ) : (
                      <AlarmClock className="h-3.5 w-3.5" />
                    )}
                  </span>
                ) : (
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-secondary text-primary dark:text-[hsl(var(--ring))]">
                    {entry.data.team ? <UsersRound className="h-3.5 w-3.5" /> : <UserPlus className="h-3.5 w-3.5" />}
                  </span>
                )}
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[13px] leading-snug">{entryLine(entry)}</p>
                  <p className="truncate text-xs text-muted-foreground">
                    #
                    {entry.type === "mention"
                      ? entry.data.comment?.backlog_item?.ticket_number
                      : entry.data.backlog_item?.ticket_number}{" "}
                    ·{" "}
                    {entry.type === "mention" ? entry.data.comment?.backlog_item?.task : entry.data.backlog_item?.task}
                  </p>
                </div>
                <span className="shrink-0 pt-0.5 text-[11px] font-bold text-muted-foreground/70">
                  {ageLabel(entry.created_at)}
                </span>
                {!entry.is_read && (
                  <span
                    className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary dark:bg-[hsl(var(--ring))]"
                    aria-hidden
                  />
                )}
              </div>
            ))
          )}
        </AnimatedHeight>
      </div>

      {visible.total > PANEL_CAP && (
        <div className="border-t border-border/70 px-3 py-2">
          <button type="button" onClick={() => navigate("/mentions")} className="textlink text-xs">
            Full history ({visible.total}) <ArrowRight className="ux-arr h-3.5 w-3.5" />
          </button>
        </div>
      )}
    </Card>
  );
};

export default CatchUpPanel;
